import React, { Fragment, ReactNode } from "react";
import { PreScreeningCaptureItemSpec } from "@pages/home/preprocess/PreScreening";
import {
  CoStringSpec,
  CrfCheckItemSpec,
  CrfItemCommonDescription,
  CrfSingleSelectItemSpec,
  InitialCrfPageSequence,
  InitialItemSequence,
  InitialVisitSequence,
} from "maven-lib/dist/consts/edc";
import { EcoaQuestionnaire } from "maven-lib/dist/dto/entity/ecoaVisit";
import { VisitQuestionnaireItem } from "../../ecoa/questionnaire/VisitQuestionnaireItem";
import { VisitQuestionnaireWrapper } from "../../ecoa/questionnaire/VisitQuestionnaireWrapper";
import { HbDivider } from "@maven-surface/components/base/HbDivider";
import { HbParagraph } from "@maven-surface/components/base/HbParagraph";

interface PreScreeningMultipleQuestionnaireProps {
  screeningCaptureData: Array<{
    itemId: string | null;
    dbVal: string | number | null;
    sectionId: string;
  }>;
  questionnaires: Array<EcoaQuestionnaire>;
  onChangeAnswers: (answers: Array<PreScreeningCaptureItemSpec>) => void;
  onChangeDisabledFooter: (val: boolean) => void;
  children: ReactNode;
}

export function PreScreeningMultipleQuestionnaire({
  screeningCaptureData,
  onChangeAnswers,
  onChangeDisabledFooter,
  questionnaires,
  children,
}: PreScreeningMultipleQuestionnaireProps) {
  const headQuestionnaireTitle = questionnaires?.length ? questionnaires[0]?.title || "" : "";

  const handleChangeAnswers = (answers: Array<{ itemId: string | null; dbVal: string | number | null }>): void => {
    const { folderId, visitId, crfPageId, sectionId } = questionnaires[0].item;
    onChangeAnswers(
      answers.map((ans) => ({
        folderId,
        visitId,
        visitSeq: InitialVisitSequence,
        crfPageId,
        crfPageSeq: InitialCrfPageSequence,
        sectionId,
        itemId: ans.itemId || "",
        itemSeq: InitialItemSequence,
        dbVal: ans.dbVal,
      }))
    );
  };

  return (
    <VisitQuestionnaireWrapper title={headQuestionnaireTitle}>
      {questionnaires.map((questionnaire, questionnaireIdx) => {
        const defaultValues = ((questionnaire) => {
          if (questionnaire.item.original.type === "CHECK") {
            const checkItem: CrfCheckItemSpec = questionnaire.item.original;
            const item = checkItem.itemCode.codes.map(({ id: itemId }) => ({
              itemId,
              dbVal: screeningCaptureData.find((item) => item.itemId === itemId)?.dbVal || null,
            }));
            const typeTextItems = checkItem.itemCode.codes
              .map(({ typeText }) => ({
                itemId: typeText?.id || "",
                dbVal: screeningCaptureData.find((item) => item.itemId === typeText?.id || "")?.dbVal || null,
              }))
              .filter(({ itemId }) => itemId !== "");

            return [...item, ...typeTextItems];
          } else if (questionnaire.item.original.type === "SINGLE_SELECT") {
            const selectItem: CrfSingleSelectItemSpec = questionnaire.item.original;
            const typeTextItems = selectItem.itemCode.codes
              .map(({ typeText }) => ({
                itemId: typeText?.id || "",
                dbVal: screeningCaptureData.find((item) => item.itemId === typeText?.id || "")?.dbVal || null,
              }))
              .filter(({ itemId }) => itemId !== "");

            return [
              {
                itemId: questionnaire.item.id,
                dbVal: screeningCaptureData.find((item) => item.itemId === questionnaire.item.id)?.dbVal || null,
              },
              ...typeTextItems,
            ];
          } else {
            return [
              {
                itemId: questionnaire.item.id,
                dbVal: screeningCaptureData.find((item) => item.itemId === questionnaire.item.id)?.dbVal || null,
              },
            ];
          }
        })(questionnaire);

        const descriptionText = (questionnaire?.item?.original?.description as CoStringSpec)?.text || "";
        const descriptionPosition = (questionnaire?.item?.original?.description as CrfItemCommonDescription)?.ecoa?.position || "BOTTOM";
        const descriptionColor = ((color: any) =>
          color === "RED" ? "DANGER" : color === "GRAY" ? "GRAY" : color === "BLUE" ? "PRIMARY" : undefined)(
          (questionnaire?.item?.original?.description as CoStringSpec)?.color
        );

        return (
          <Fragment key={questionnaireIdx}>
            {descriptionText && descriptionPosition === "TOP" && (
              <>
                <HbParagraph align={"LEFT"} size={"LARGE"} color={descriptionColor} bold={false}>
                  {descriptionText}
                </HbParagraph>
                <HbDivider />
              </>
            )}
            {questionnaire.question && (
              <HbParagraph
                text={questionnaire.question}
                size="3X-LARGE"
                bold={true}
                color="GRAY"
                icon={questionnaire.item.original?.labelIcon}
              />
            )}
            {questionnaire.text && <HbParagraph text={questionnaire.text} size="X-LARGE" bold={true} />}
            <HbDivider />
            <VisitQuestionnaireItem
              isAvailable
              questionnaire={questionnaire}
              defaultItemValues={defaultValues}
              onValueChange={handleChangeAnswers}
              onChangeDisabledFooter={onChangeDisabledFooter}
            />
            <HbDivider />
            {descriptionText && descriptionPosition === "BOTTOM" && (
              <HbParagraph align={"LEFT"} size={"LARGE"} color={descriptionColor} bold={false}>
                {descriptionText}
              </HbParagraph>
            )}
          </Fragment>
        );
      })}
      {children}
    </VisitQuestionnaireWrapper>
  );
}
